import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import {
	AnnouncementInterests,
	LiveInformationAnnouncementRequestModel,
	LiveInformationAnnouncementResponseModel,
	LiveInformationAnnouncementResponseModelData,
	list,
	listMore
} from "client/Api/Workspace/LiveInformation/Hub/announcementsLiveInformation";
import {
	HubItemCategoryIdentifierModel
} from "client/Api/Hub/common";
import { Guid } from "client_legacy/ndaw/guid";
import {
	AnnouncementPagingInformationModel,
	AnnouncementPreviewItemModel
} from "client/Api/Hub/Announcements/announcementViewer";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { HubTileCategoryIds } from "client/Features/Workspace/Tiles/Hub/common";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { ensureNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { HubItemTileResourceId, HubItemTileResourceKind } from "client/Api/Workspace/workspace";
import { listCategories } from "client/Api/Workspace/LiveInformation/Hub/hubItemCategoriesLiveInformation";

interface InterestedAnnouncementParty extends InterestedParty<Guid, HubItemTileResourceId> {
	updateItems(page: LazyLoadPageResult<AnnouncementPreviewItemModel>): void;
	updateLastPinnedItem(lastPinnedItem: AnnouncementPreviewItemModel | null): void;
}

type AnnouncementsLiveInformationProviderOptions = {
	intervalInSeconds: number;
};

class AnnouncementsLiveInformationProvider extends LiveInformationProvider<
	AnnouncementsLiveInformationProviderOptions,
	Guid,
	HubItemTileResourceId,
	AnnouncementInterests,
	InterestedAnnouncementParty,
	LiveInformationAnnouncementResponseModelData
>
{
	private _listRetrievalPromise: Promise<HubItemCategoryIdentifierModel[]> | undefined;

	public get itemKind(): HubItemKind {
		return HubItemKind.Announcement;
	}

	constructor(options: AnnouncementsLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[HubItemTileResourceId, AnnouncementInterests]>
	): Promise<ReadonlyArray<[HubItemTileResourceId, LiveInformationAnnouncementResponseModelData]>> {
		const requests: LiveInformationAnnouncementRequestModel[] = [];

		entries.forEach(x => {
			const [resource, interests] = x;

			requests.push({
				resource,
				interests
			});
		});

		const announcementsWithCategoryList: LiveInformationAnnouncementResponseModel[] = await list(requests);

		const result: Array<[HubItemTileResourceId, LiveInformationAnnouncementResponseModelData]> = announcementsWithCategoryList.map(x => {
			const item: [HubItemTileResourceId, LiveInformationAnnouncementResponseModelData] = [
				x.resource,
				x.data
			];

			return item;
		});

		return result;
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<AnnouncementInterests, LiveInformationAnnouncementResponseModelData>,
		response: [AnnouncementInterests, LiveInformationAnnouncementResponseModelData]
	): void {
		const [retrievedInterests, data] = response;

		if (retrievedInterests.items) {
			cacheItem.retrievedInterests.items = true;
			cacheItem.data.items = data.items;
		}

		if (retrievedInterests.lastPinnedItem) {
			cacheItem.retrievedInterests.lastPinnedItem = true;
			cacheItem.data.lastPinnedItem = data.lastPinnedItem;
		}
	}

	protected informParty(
		party: InterestedAnnouncementParty,
		cacheItem: [AnnouncementInterests, LiveInformationAnnouncementResponseModelData]
	): void {
		const [retrievedInterests, data] = cacheItem;
		const interestedIn: AnnouncementInterests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.items && retrievedInterests.items) {
			party.updateItems(
				ensureNotNil(data.items)
			);
		}

		if (interestedIn.lastPinnedItem && retrievedInterests.lastPinnedItem) {
			party.updateLastPinnedItem(data.lastPinnedItem);
		}
	}

	protected override createResourceMap<TValue>(): Map<HubItemTileResourceId, TValue> {
		return new NormalizedMap<HubItemTileResourceId, TValue>(
			normalizeKey
		);
	}

	public reload(party: InterestedAnnouncementParty) {
		this.clearCacheForResource(party.resourceId);
		const interests: AnnouncementInterests = this.getInterestsForEnsuredParty(party);
		this.queuePartyForDataRetrieval(party, interests);
	}

	public listCategories(): Promise<HubItemCategoryIdentifierModel[]> {
		if (!this._listRetrievalPromise) {
			this._listRetrievalPromise = listCategories(HubItemKind.Announcement);
		}
		return this._listRetrievalPromise;
	}

	public loadMoreItems(
		categoryIds: HubTileCategoryIds,
		interests: Required<AnnouncementInterests>,
		lastRetrievedItem: AnnouncementPreviewItemModel,
		signal?: AbortSignal
	)
		: Promise<LazyLoadPageResult<AnnouncementPreviewItemModel>>
	{
		const {
			comments,
			reactions
		} = interests;

		const paginationInfo: AnnouncementPagingInformationModel = {
			id: lastRetrievedItem.id,
			startTime: lastRetrievedItem.publicationStart,
			viewsCount: lastRetrievedItem.viewsCount,
			pinDate: lastRetrievedItem.pinDate
		};

		return listMore(
			{
				categoryIds,
				comments,
				reactions,
				lastRetrievedItem: paginationInfo
			},
			signal
		);
	}
}

function normalizeKey(key: HubItemTileResourceId): string {
	switch (key.kind) {
		case HubItemTileResourceKind.CategoryIds:
			if (key.categoryIds === null) {
				return "categories|all";
			} else {
				return `categories|${key.categoryIds.join()}`;
			}
		case HubItemTileResourceKind.ItemIds:
			return `items|${key.itemIds.join()}`;
		default:
			never(key);
	}
}

export {
	AnnouncementsLiveInformationProvider
};
